import { KeycloakLoginOptions } from 'keycloak-js';
import { STORAGE_KEY_TOKEN, STORAGE_KEY_REFRESH_TOKEN } from '../auth.constant';
import { ITokens } from '../auth.interface';

const STORAGE_KEY_CALLBACK_PREFIX = 'kc-callback-';

export interface ICallbackState {
  state: string;
  redirectUri: string;
  loginOptions?: KeycloakLoginOptions;
}

/**
 * Clears all values from local storage that are no longer valid.
 */
function clearInvalidValues() {
  const currentTime = Date.now();

  for (const [key, value] of getStoredEntries()) {
    // Attempt to parse the expiry time from the value.
    const expiry = parseExpiry(value);

    // Discard the value if it is malformed or expired.
    if (expiry === null || expiry < currentTime) {
      localStorage.removeItem(key);
    }
  }
}

/**
 * Clears all known values from local storage.
 */
function clearAllValues() {
  for (const [key] of getStoredEntries()) {
    localStorage.removeItem(key);
  }
}

/**
 * Gets all entries stored in local storage that are known to be managed by this class.
 * @returns {Array<[string, unknown]>} An array of key-value pairs.
 */
function getStoredEntries(): [string, unknown][] {
  return Object.entries(localStorage).filter(([key]) => key.startsWith(STORAGE_KEY_CALLBACK_PREFIX));
}

/**
 * Parses the expiry time from a value stored in local storage.
 * @param {unknown} value
 * @returns {number | null} The expiry time in milliseconds, or `null` if the value is malformed.
 */
function parseExpiry(value): number | null {
  let parsedValue;

  // Attempt to parse the value as JSON.
  try {
    parsedValue = JSON.parse(value);
  } catch (error) {
    return null;
  }

  // Attempt to extract the 'expires' property.
  if (typeof parsedValue === 'object' && parsedValue !== null && 'expires' in parsedValue && typeof parsedValue.expires === 'number') {
    return parsedValue.expires;
  }

  return null;
}

export const addStateToLocalStorage = (state: ICallbackState) => {
  clearInvalidValues();

  const key = STORAGE_KEY_CALLBACK_PREFIX + state.state;
  const value = JSON.stringify({
    ...state,
    // Set the expiry time to 1 hour from now.
    expires: Date.now() + (60 * 60 * 1000),
  });

  try {
    localStorage.setItem(key, value);
  } catch (error) {
    // If the storage is full, clear all known values and try again.
    clearAllValues();
    localStorage.setItem(key, value);
  }
};

export const addTokenToLocalStorage = ({ token, refreshToken }: ITokens) => {
  try {
    if (token) {
      localStorage.setItem(STORAGE_KEY_TOKEN, token);
    }
    if (refreshToken) {
      localStorage.setItem(STORAGE_KEY_REFRESH_TOKEN, refreshToken);
    }
  } catch (error) { /* noop */ }
};

export const removeTokenFromLocalStorage = () => {
  localStorage.removeItem(STORAGE_KEY_TOKEN);
  localStorage.removeItem(STORAGE_KEY_REFRESH_TOKEN);
};

export const getTokenFromLocalStorage = (): ITokens => {
  return {
    token: localStorage.getItem(STORAGE_KEY_TOKEN),
    refreshToken: localStorage.getItem(STORAGE_KEY_REFRESH_TOKEN),
  };
};
